import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export function LevelsFilter() {
  const nav = useNavigate({ from: "/app/master/levels" });
  const search = useSearch({
    from: "/app/master/levels",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";
  const hasFilter = !!nameFilter?.value || !!statusValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາລະດັບ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />

          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
